// SPDX-License-Identifier: GPL-3.0-or-later
// Copyright (C) 2026 Radiooooo

import { createLayout } from "material-addons/layout";
import type { LoadingIndicatorComponent } from "material";
import type { I18n } from "../core/i18n";
import { bindStrings, withStrings, type Strings } from "../core/translate";
import type { Schema, Translate } from "./frame";
import { pick } from "./layout";

/**
 * The signed-out landing: the card with the sign-in form. It is what a
 * visitor gets, and all a visitor gets. The frame arrives with the session
 * (decision 0002), so the landing mounts on its own, before any of it, and
 * covers the whole app once the frame is there: shown again at sign-out,
 * over the frame that stays.
 */
export function landingSchema(): Schema {
    return [
        ["landing", { rawClass: "landing", attributes: { role: "region", hidden: "" } },
            // the brand mark where the application's head puts it, the same place and size
            ["logo", { rawClass: "landing__logo", attributes: { role: "img" } }],
            ["card", { rawClass: "landing__card" },
                ["signin", { rawClass: "landing__signin" }],
                // while the session is restored, or the frame fetched after a sign-in: mtrl's indicator, named "Connecting…"
                ["connecting", { rawClass: "landing__connecting", attributes: { hidden: "", role: "status" } }],
            ],
        ],
    ];
}

/** The landing's words, by node (core/translate). */
export const LANDING_STRINGS: Strings = {
    landing: { ariaLabel: "landing.title" },
    logo: { ariaLabel: "landing.title" },
    connecting: { ariaLabel: "landing.connecting" },
};

export interface Landing {
    /** Where the sign-in form renders. */
    slots: { signin: HTMLElement };
    /** Covers whatever is under it, the frame included, or lifts. */
    show(visible: boolean): void;
    /** While the boot restore runs: no form, only a "connecting" line. */
    setBusy(busy: boolean): void;
    destroy(): void;
}

export function mountLanding(root: HTMLElement, i18n: I18n): Landing {
    const t: Translate = (key) => i18n.t(key);
    const layout = createLayout(withStrings(landingSchema(), LANDING_STRINGS, t), root, { prefix: false });
    const offStrings = bindStrings(i18n, layout, LANDING_STRINGS);
    const ui = pick<{ landing: HTMLElement; signin: HTMLElement; connecting: HTMLElement }>("landing", layout, ["landing", "signin", "connecting"]);
    /**
     * mtrl's loading indicator, fetched with the first busy moment rather
     * than with the entry: a visitor's landing stays as light as it is, and a
     * returning browser has the chunk preloaded for the frame anyway.
     */
    let indicator: LoadingIndicatorComponent | undefined;
    let fetching: Promise<void> | undefined;
    let busy = false;
    let destroyed = false;
    function spin() {
        if (!busy) { indicator?.stop(); return; }
        if (indicator) { indicator.start(); return; }
        fetching ??= import("material/components/loading-indicator").then(({ createLoadingIndicator }) => {
            if (destroyed) return;
            indicator = createLoadingIndicator({ size: 36, ariaLabel: t("landing.connecting") });
            ui.connecting.append(indicator.element);
            if (busy) indicator.start();
        }).catch(() => { fetching = undefined; });
    }
    // the root is the app, whether or not the frame is in it yet
    root.classList.add("app");
    /**
     * The landing is v3's, the legacy theme in light, whatever theme and mode
     * the app is on: held on the document while the landing is on screen,
     * and the app's own put back when it lifts (the frame's appearance store
     * writes the same attributes on its next change; this covers the gap).
     */
    const html = document.documentElement;
    let held: { theme: string | null; mode: string | null; interfaces: string[] } | undefined;
    function hold(on: boolean) {
        if (on && !held) {
            held = { theme: html.getAttribute("data-theme"), mode: html.getAttribute("data-theme-mode"), interfaces: [...html.classList].filter((name) => name.startsWith("theme-")) };
            html.setAttribute("data-theme", "legacy");
            html.setAttribute("data-theme-mode", "light");
            // and v3's interface: the card is the fancy one whatever the app is on (its stylesheet is loaded regardless, styles/index)
            for (const name of held.interfaces) html.classList.remove(name);
            html.classList.add("theme-fancy");
        } else if (!on && held) {
            const back = held;
            held = undefined;
            if (back.theme) html.setAttribute("data-theme", back.theme); else html.removeAttribute("data-theme");
            if (back.mode) html.setAttribute("data-theme-mode", back.mode); else html.removeAttribute("data-theme-mode");
            html.classList.remove("theme-fancy");
            for (const name of back.interfaces) html.classList.add(name);
        }
    }
    return {
        slots: { signin: ui.signin },
        show(visible) {
            ui.landing.hidden = !visible;
            root.classList.toggle("app--landing", visible);
            hold(visible);
        },
        setBusy(on) {
            busy = on;
            ui.signin.hidden = on;
            ui.connecting.hidden = !on;
            spin();
        },
        destroy() {
            destroyed = true;
            hold(false);
            indicator?.destroy();
            offStrings();
            layout.destroy();
            ui.landing.remove();
            root.classList.remove("app--landing");
        },
    };
}
